import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, FileText, ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { QmsAccountSelect } from '@/features/qms/components/shared/QmsAccountSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useQuotes } from '@/features/operations-projects';
import type { Quote } from '@/features/operations-projects';
import { useCreateRma } from '../../hooks/useRma';
import type { CpServiceCode } from '../../types';
import { CP_SERVICE_LABELS } from '../../types';

interface CreateRmaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateRmaModal({ open, onOpenChange }: CreateRmaModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('operations');
  const createRma = useCreateRma();

  const [accountId, setAccountId] = useState('');
  const [reasonNotes, setReasonNotes] = useState('');
  const [serviceCode, setServiceCode] = useState<CpServiceCode>('DOM.RP');

  // Quote selector state
  const [quoteSearch, setQuoteSearch] = useState('');
  const [quotePopoverOpen, setQuotePopoverOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  const { data: quotesData, isLoading: quotesLoading } = useQuotes(
    {
      search: quoteSearch || undefined,
      accountId: accountId || undefined,
      pageLength: 50,
      sortKey: 'createdAt',
      sortDirection: -1,
    },
    { enabled: open },
  );
  const quotes = useMemo(() => quotesData?.quotes || [], [quotesData]);

  const handleQuoteSelect = (quote: Quote) => {
    setSelectedQuote(quote);
    setQuotePopoverOpen(false);
    if (quote.account_id) setAccountId(quote.account_id);
  };

  const canSubmit = !!accountId;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    createRma.mutate(
      {
        accountId,
        quoteId: selectedQuote?.id || undefined,
        quoteNumber: selectedQuote?.quote_number || undefined,
        reasonNotes: reasonNotes || undefined,
        cpServiceCode: serviceCode,
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          navigate(`/operations/rma/${data.id}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('rma.modal.title', 'Create New RMA')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Account */}
          <div className="space-y-2">
            <Label>{t('rma.modal.account', 'Account')}</Label>
            <QmsAccountSelect
              value={accountId || undefined}
              onChange={(id) => {
                const next = id || '';
                setAccountId(next);
                if (selectedQuote && selectedQuote.account_id !== next) {
                  setSelectedQuote(null);
                  setQuoteSearch('');
                }
              }}
            />
          </div>

          {/* Quote Selector */}
          <div className="space-y-2">
            <Label>{t('rma.modal.linkQuote', 'Link to Quote (optional)')}</Label>
            <Popover open={quotePopoverOpen} onOpenChange={setQuotePopoverOpen} modal={false}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={quotePopoverOpen}
                  className="w-full justify-between"
                >
                  {selectedQuote ? (
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {selectedQuote.quote_number}
                      {selectedQuote.account_name && ` - ${selectedQuote.account_name}`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t('rma.modal.searchQuote', 'Search for a quote...')}</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[460px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder={t('rma.modal.searchQuoteInput', 'Search by quote number or account...')}
                    value={quoteSearch}
                    onValueChange={setQuoteSearch}
                  />
                  <CommandList>
                    {quotesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">{t('rma.modal.loadingQuotes', 'Loading quotes...')}</span>
                      </div>
                    ) : quotes.length === 0 ? (
                      <CommandEmpty>{t('rma.modal.noQuotes', 'No quotes found.')}</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {quotes.map((quote) => (
                          <CommandItem
                            key={quote.id}
                            value={quote.id}
                            onSelect={() => handleQuoteSelect(quote)}
                            className="flex items-center py-2"
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedQuote?.id === quote.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">
                                {quote.quote_number}
                                {quote.account_name && ` - ${quote.account_name}`}
                              </div>
                              {quote.project_name && (
                                <div className="text-sm text-muted-foreground truncate">
                                  {quote.project_name}
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground ml-2">{quote.status}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reason Notes */}
          <div className="space-y-2">
            <Label htmlFor="reasonNotes">{t('rma.modal.notes', 'Notes (optional)')}</Label>
            <Textarea
              id="reasonNotes"
              placeholder={t('rma.modal.notesPlaceholder', 'Additional details about the return...')}
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Service Code */}
          <div className="space-y-2">
            <Label>{t('rma.modal.serviceCode', 'Service Code')}</Label>
            <Select value={serviceCode} onValueChange={(v) => setServiceCode(v as CpServiceCode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CP_SERVICE_LABELS) as CpServiceCode[]).map((code) => (
                  <SelectItem key={code} value={code}>
                    {t(`rma.service.${code}`, CP_SERVICE_LABELS[code])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('rma.modal.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || createRma.isPending}>
              {createRma.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t('rma.modal.submit', 'Create RMA')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
